import { apiRequest } from './axiosConfig';

// Response types
export interface UsageSummary {
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalImageCount: number;
  totalRequests: number;
  estimatedCostUsd: number;
  totalCredits: number;
  byFeature: {
    [key: string]: {
      tokens: number;
      requests: number;
      imageCount: number;
      costUsd: number;
      credits: number;
    };
  };
  byProvider: {
    [key: string]: {
      tokens: number;
      requests: number;
      imageCount: number;
      costUsd: number;
      credits: number;
    };
  };
}

export interface DailyUsage {
  date: string;
  total_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_image_count: number;
  total_requests: number;
  estimated_cost_usd: number;
  total_credits: number;
  by_feature: {
    [key: string]: {
      tokens: number;
      requests: number;
      image_count: number;
      cost_usd: number;
      credits: number;
    };
  };
}

export interface FeatureUsage {
  feature_type: string;
  feature_label: string;
  total_requests: number;
  total_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_image_count: number;
  estimated_cost_usd: number;
  total_credits: number;
}

export interface UsageLog {
  id: number;
  user_id: number;
  organization_id: number | null;
  feature_type: string;
  service_provider: string;
  model_name: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  image_count: number;
  resolution: string | null;
  estimated_cost_usd: number;
  credits: number;
  metadata: Record<string, any>;
  related_id: number | null;
  related_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeatureType {
  type: string;
  label: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  period?: {
    type?: string;
    days?: number;
    start_date: string;
    end_date: string;
  };
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export type PeriodType = 'day' | 'week' | 'month' | 'year';

// Usage API Service
const usageService = {
  /**
   * Get usage summary for the current user
   */
  getSummary: async (period: PeriodType = 'month', featureType?: string): Promise<ApiResponse<UsageSummary>> => {
    const params = new URLSearchParams({ period });
    if (featureType) {
      params.append('feature_type', featureType);
    }
    return apiRequest.get<ApiResponse<UsageSummary>>(`usage/summary?${params.toString()}`);
  },

  /**
   * Get daily usage breakdown
   */
  getDaily: async (days: number = 30): Promise<ApiResponse<DailyUsage[]>> => {
    return apiRequest.get<ApiResponse<DailyUsage[]>>(`usage/daily?days=${days}`);
  },

  /**
   * Get usage grouped by feature type
   */
  getByFeature: async (period: PeriodType = 'month'): Promise<ApiResponse<FeatureUsage[]>> => {
    return apiRequest.get<ApiResponse<FeatureUsage[]>>(`usage/by-feature?period=${period}`);
  },

  /**
   * Get paginated usage logs
   */
  getLogs: async (page: number = 1, perPage: number = 20, featureType?: string): Promise<ApiResponse<UsageLog[]>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    if (featureType) {
      params.append('feature_type', featureType);
    }
    return apiRequest.get<ApiResponse<UsageLog[]>>(`usage/logs?${params.toString()}`);
  },

  /**
   * Get organization-wide usage (for admins)
   */
  getOrganization: async (period: PeriodType = 'month'): Promise<ApiResponse<UsageSummary>> => {
    return apiRequest.get<ApiResponse<UsageSummary>>(`usage/organization?period=${period}`);
  },

  /**
   * Get available feature types with labels
   */
  getFeatureTypes: async (): Promise<ApiResponse<FeatureType[]>> => {
    return apiRequest.get<ApiResponse<FeatureType[]>>('usage/feature-types');
  },
};

export default usageService;
